import {
  useCreditsStore,
  selectAvailableCenti,
  selectBalanceCredits,
  selectCreditsPhase,
  formatCredits,
} from '../state/creditsStore';

// ════════════════════════════════════════════════════════════════════════════
// PortalBalanceTab — balance & top-up surface (valko-credits-platform Phase 3).
//
// Pure renderer of the creditsStore wallet region (BalancePill precedent). Reads
// selectors ONLY — it never synthesizes a balance. The "Top up" CTA drives the
// already-mounted <TopUpModal/> (App.tsx) via openTopUpModal(null); the pack
// cards live in that modal and are NOT duplicated here.
//
// DARK-BY-DEFAULT contract: when the wallet is 'uninitialized' (no CREDIT_BALANCE
// this session — dogfood tiers that never emit, or CREDITS_ENFORCE=false),
// selectAvailableCenti is null → render a neutral "no balance yet" state, NEVER a
// literal 0 credits (which would imply a real empty wallet). Matches BalancePill's
// dark-by-default behavior. The "Top up" button stays available so the user can
// still purchase credits before the server has spoken.
// ════════════════════════════════════════════════════════════════════════════

// Read-only teaser mirroring the TopUpModal PACKS (which mirror backend
// CREDIT_PACKS). Display-only — the actual buy happens in the modal.
const PACK_TEASERS: Array<{ eur: number; credits: string; bonusPct: number }> = [
  { eur: 5, credits: formatCredits(5000), bonusPct: 0 },
  { eur: 15, credits: formatCredits(15750), bonusPct: 5 },
  { eur: 40, credits: formatCredits(44000), bonusPct: 10 },
  { eur: 100, credits: formatCredits(120000), bonusPct: 20 },
];

const cardStyle: Record<string, string | number> = {
  background: 'var(--bg-elevated, #1a1a2e)',
  border: '1px solid var(--border-subtle, #2a2a44)',
  borderRadius: 10,
  padding: '18px 20px',
};

export function PortalBalanceTab() {
  const availCenti = useCreditsStore(selectAvailableCenti);
  const balanceCredits = useCreditsStore(selectBalanceCredits);
  const phase = useCreditsStore(selectCreditsPhase);
  const openTopUpModal = useCreditsStore((s) => s.openTopUpModal);
  const heldCenti = useCreditsStore((s) => {
    const w = s.activeUserId ? s.wallets[s.activeUserId] : undefined;
    return w && w.live ? w.heldCenti : 0;
  });

  const live = availCenti != null; // wallet has spoken this session
  const low = phase === 'low';
  const held = heldCenti > 0 ? formatCredits(heldCenti) : null;

  return (
    <div class="admin-content" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Balance card */}
      <div style={cardStyle}>
        <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-dim, #5a5a7e)', marginBottom: 8 }}>
          Available balance
        </div>
        {live ? (
          <>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span style={{ fontSize: 34, fontWeight: 700, color: low ? 'var(--accent-red, #f4b942)' : 'var(--text-primary, #c8c8e8)' }}>
                {balanceCredits}
              </span>
              <span style={{ fontSize: 15, color: 'var(--text-secondary, #8888aa)' }}>credits</span>
            </div>
            {held && (
              <div style={{ fontSize: 12, color: 'var(--text-dim, #5a5a7e)', marginTop: 6 }}>
                {held} credits held by in-flight work
              </div>
            )}
            {low && (
              <div style={{ fontSize: 12, color: 'var(--accent-red, #f4b942)', marginTop: 6 }}>
                Your balance is running low.
              </div>
            )}
          </>
        ) : (
          <div style={{ fontSize: 14, color: 'var(--text-secondary, #8888aa)', lineHeight: 1.5 }}>
            No credit balance yet.
            <div style={{ fontSize: 12, color: 'var(--text-dim, #5a5a7e)', marginTop: 4 }}>
              Your balance appears here once credits are active on your account.
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={() => openTopUpModal(null)}
          style={{
            marginTop: 16,
            background: 'var(--theme-accent, #6666cc)',
            color: 'var(--text-on-accent, #fff)',
            border: 'none',
            borderRadius: 8,
            padding: '10px 18px',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Top up
        </button>
      </div>

      {/* Read-only pack teaser (the real purchase happens in TopUpModal) */}
      <div>
        <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-dim, #5a5a7e)', marginBottom: 8 }}>
          Prepaid packs
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 10 }}>
          {PACK_TEASERS.map((p) => (
            <div
              key={p.eur}
              style={{
                background: 'var(--bg-elevated, #1a1a2e)',
                border: '1px solid var(--border-subtle, #2a2a44)',
                borderRadius: 8,
                padding: '12px 14px',
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
              }}
            >
              <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary, #c8c8e8)' }}>€{p.eur}</span>
              <span style={{ fontSize: 13, color: 'var(--text-secondary, #8888aa)' }}>{p.credits} cr</span>
              {p.bonusPct > 0 && (
                <span class="tum-pack__bonus">+{p.bonusPct}% bonus</span>
              )}
            </div>
          ))}
        </div>
        <p style={{ fontSize: 11, color: 'var(--text-dim, #5a5a7e)', marginTop: 10 }}>
          Prepaid credits — no subscription. Tap “Top up” to purchase; credits are added on payment.
        </p>
      </div>
    </div>
  );
}
